/**
 * safeProfile.js
 * 
 * Shared null-safety helpers for profile objects.
 * Prevents crashes from null/undefined list fields (names, interests, photos, etc.)
 * 
 * Usage:
 *   import { safeArr, safeJoin, getDisplayName } from '../utils/safeProfile';
 *   
 *   safeArr(profile.interests).map(...)
 *   safeJoin(profile.languages)
 *   getDisplayName(profile)
 */

/**
 * Convert null/undefined to empty array
 * @param {any} v - Value to check
 * @returns {Array} - Original array or empty array
 */
export const safeArr = (v) => (Array.isArray(v) ? v : []);

/**
 * Safe join that handles null/undefined arrays
 * @param {any} v - Value to join
 * @param {string} sep - Separator (default ", ")
 * @returns {string} - Joined string or empty string
 */
export const safeJoin = (v, sep = ", ") => safeArr(v).join(sep);

/**
 * Get display name from profile, handling couples and singles
 * @param {object} profile - Profile object
 * @param {string} fallback - Fallback name (default "Someone")
 * @returns {string} - Display name
 */
export const getDisplayName = (profile, fallback = "Someone") => {
  if (!profile) return fallback;
  
  const names = safeArr(profile.names);
  
  // Couple: 2+ names joined with "&"
  if (names.length >= 2) {
    return `${names[0]} & ${names[1]}`;
  }
  
  // Single name in array
  if (names.length === 1) {
    return names[0];
  }
  
  // Fallback to display_name or name field
  if (profile.display_name) return profile.display_name;
  if (profile.name) return profile.name;
  if (profile.first_name) return profile.first_name;
  
  return fallback;
};

/**
 * Get first initial from profile name (for avatars)
 * @param {object} profile - Profile object
 * @returns {string} - First initial or "?"
 */
export const getInitial = (profile) => {
  if (!profile) return "?";
  
  const names = safeArr(profile.names);
  if (names.length > 0 && names[0]) {
    return names[0].charAt(0).toUpperCase();
  }
  
  const displayName = profile.display_name || profile.name || "";
  return displayName.charAt(0).toUpperCase() || "?";
};

/**
 * Check if profile represents a couple
 * Uses explicit flags first, then falls back to names.length check
 * @param {object} profile - Profile object
 * @returns {boolean} - True if couple
 */
export const isCouple = (profile) => {
  if (!profile) return false;
  
  // Priority 1: Explicit user_type/relationship_type flags
  const userType = (profile.user_type || "").toLowerCase();
  const relType = (profile.relationship_type || "").toLowerCase();
  if (userType === "couple" || relType === "couple") return true;
  
  // Priority 2: Couple-specific IDs
  if (profile.couple_id || profile.on_behalf_of_couple_id) return true;
  
  // Priority 3: Gender field
  const gender = (profile.gender || "").toLowerCase();
  if (gender === "couple" || gender === "couples") return true;
  
  // Priority 4: Names array fallback (2+ names = couple)
  const names = safeArr(profile.names);
  if (names.length >= 2) return true;
  
  return false;
};

export default {
  safeArr,
  safeJoin,
  getDisplayName,
  getInitial,
  isCouple,
};
